import { action, observable, computed } from 'mobx';
import { deserialise } from 'kitsu-core';

import FractionType from 'types/model/fraction';
import MillingMachineType from 'types/model/millingMachine';
import { ReasonGet } from 'types/model/reason';

import Fraction from 'productionOrders/models/Fraction';
import RoastingResult from 'types/utils/roastingResult';
import {
  loadExpandedIds,
  saveExpandedIds,
} from 'productionOrders/utils/fractionsExpansion';

export type BagWeightSource = 'contract_packaging' | 'country_export_size';

// PLA-860 v3 (D4): estado inicial de las filas con hijas y clave para recordarlo.
export interface FractionsExpansionOptions {
  collapsed: boolean;
  storageKey?: string;
}

class FractionsStore {
  public estimatedWeight: number;
  public maxWeight: number;
  public fractions = observable.array<Fraction>();
  public roastingResults: RoastingResult[];
  public roastingValidations: { pppt: { min: number; max: number } };
  public humidityAnalysisAcceptingReasons: ReasonGet[];
  public machines: MillingMachineType[];
  public selectedSamples = observable.array<number>();
  // PLA-860 v2 (V3): peso del saco para "Dividir por sacos" y su origen.
  public bagWeightKg?: number | null;
  public bagWeightSource?: BagWeightSource | null;
  // PLA-860 v3 (D1): ids de las fracciones y subdivisiones abiertas.
  public expandedIds = observable.set<string>();
  private expansionStorageKey?: string;

  constructor(
    estimatedWeight: number,
    maxWeight: string,
    fractions: FractionType[],
    roastingResults: RoastingResult[],
    roastingValidations: { pppt: { min: number; max: number } },
    humidityAnalysisAcceptingReasons: ReasonGet[],
    machines: MillingMachineType[],
    bagWeightKg?: number | null,
    bagWeightSource?: BagWeightSource | null,
    expansion: FractionsExpansionOptions = { collapsed: false }
  ) {
    this.bagWeightKg = bagWeightKg;
    this.bagWeightSource = bagWeightSource;
    this.maxWeight = maxWeight ? parseFloat(maxWeight) : 0;
    this.estimatedWeight = estimatedWeight;

    const deserializedFractions = deserialise(fractions).data.map(
      (fraction) => new Fraction(fraction, undefined, undefined, this)
    );

    this.machines = machines ? deserialise(machines).data : [];
    this.roastingResults = deserialise(roastingResults).data as RoastingResult[];
    this.humidityAnalysisAcceptingReasons = humidityAnalysisAcceptingReasons;
    this.fractions = observable.array(deserializedFractions);
    this.roastingValidations = roastingValidations;

    this.initExpansion(expansion);
  }

  // PLA-860 v3 (D4, W3, W4): lo recordado o, si no hay, todo abierto en "Finalizar" y todo
  // cerrado desde "Finalizar QA".
  @action private initExpansion({ collapsed, storageKey }: FractionsExpansionOptions) {
    this.expansionStorageKey = storageKey;

    const savedIds = storageKey ? loadExpandedIds(storageKey) : null;
    const expandable = this.expandableFractions;
    const ids = savedIds
      ? expandable.filter((fraction) => savedIds.includes(String(fraction.id)))
      : collapsed
      ? []
      : expandable;

    this.expandedIds.replace(ids.map((fraction) => String(fraction.id)));
  }

  // Fracciones de la corrida, en cualquier nivel, que tienen subdivisiones o reprocesos.
  @computed get expandableFractions(): Fraction[] {
    return this.fractions
      .reduce(
        (all, fraction) => all.concat(fraction, fraction.descendants),
        [] as Fraction[]
      )
      .filter((fraction) => fraction.hasChildren);
  }

  @computed get allExpanded(): boolean {
    return this.expandableFractions.every((fraction) => this.isExpanded(fraction));
  }

  public isExpanded(fraction: Fraction): boolean {
    return this.expandedIds.has(String(fraction.id));
  }

  @action public setExpanded(fraction: Fraction, expanded: boolean) {
    if (expanded) {
      this.expandedIds.add(String(fraction.id));
    } else {
      this.expandedIds.delete(String(fraction.id));
    }

    this.persistExpansion();
  }

  // PLA-860 v3 (W6): abre o cierra todas las filas con hijas de la corrida.
  @action public setAllExpanded(expanded: boolean) {
    this.expandedIds.replace(
      expanded ? this.expandableFractions.map((fraction) => String(fraction.id)) : []
    );

    this.persistExpansion();
  }

  private persistExpansion() {
    if (this.expansionStorageKey) {
      saveExpandedIds(this.expansionStorageKey, Array.from(this.expandedIds));
    }
  }

  @action remainingFractionWeight(currentFraction: Fraction) {
    const otherFractions = this.fractions.filter(
      (fraction) => fraction !== currentFraction
    );

    const otherFractionsTotalWeight = otherFractions.reduce(
      (total, fraction) => total + fraction.weight,
      0
    );

    return this.maxWeight - otherFractionsTotalWeight;
  }

  @computed get remainingExcelsoWeight(): string {
    return this.fractions
      .reduce((total, fraction) => {
        let weight = fraction.remainingWeight;

        const lastReprocess = fraction.lastReprocess;

        if (lastReprocess) {
          weight = lastReprocess.weight;
        }

        return total + weight;
      }, 0)
      .toFixed(2);
  }

  @computed get totalRejected(): string {
    return this.fractions
      .reduce((totalLoss, fraction) => {
        return totalLoss + fraction.totalRejected;
      }, 0)
      .toFixed(2);
  }

  @computed get totalLoss(): string {
    return this.fractions
      .reduce((totalLoss, fraction) => {
        return (totalLoss += fraction.totalLoss);
      }, 0)
      .toFixed(2);
  }

  @computed get selectedSamplesCount(): number {
    return this.selectedSamples.length;
  }

  @computed get roastingSamplesCount(): number {
    return this.fractions.filter((fraction) => fraction.sample?.current_roasting?.id)
      .length;
  }

  // PLA-860 v2 (D6): subdivisiones sin resultado que se aprobaran al confirmar.
  @computed get pendingSubdivisions(): Fraction[] {
    return this.fractions.reduce(
      (pending, fraction) => pending.concat(fraction.pendingSubdivisions),
      [] as Fraction[]
    );
  }

  @action public toggleSelectedLot = (sampleId: number) => {
    if (this.selectedSamples.includes(sampleId)) {
      this.selectedSamples.remove(sampleId);
    } else {
      this.selectedSamples.push(sampleId);
    }
  };
}

export default FractionsStore;
